import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ReadonlyLinkedShowsPanelSegment from "document/linkshow/ReadonlyLinkedShowsPanelSegment";
import ChangeLifecycleButtons from "eSign/components/ChangeLifecycleButtons";
import { InvoiceV2InfoSection } from "InvoiceV2/components/DocumentDetails/InvoiceV2InfoSection";
import React from "react";
import DocumentChecklistPanel from "../../checklist/DocumentChecklistPanel";
import { Modal } from "../../components";
import TwoColumnLayout from "../../layout/TwoColumnLayout";
import ReadonlyProductsPanelSegment from "../../Products/ReadonlyProductsPanelSegment";
import ESignActivityPanel from "../components/activity/InvoiceActivityPanel";
import SendOutDocument from "../components/modals/SendOutDocument";
import InvoiceV2OptionsPanel from "../components/optionspanel/InvoiceV2OptionsPanel";
import RecipientsPanel from "../panelsegment/RecipientsPanel";
import NotesPanel from "note/list/app";
import { isPanelVisible } from "utilities/panelUtils";
import { InvoiceReminderApp } from "DocumentReminder/AppForInvoices";

interface Props {
  viewModel: server.viewmodel.InvoiceV2DetailFrontModel;
}

const ReviewPanel: React.FC<Props> = ({ viewModel }) => {
  const [displayConfirmation, setDisplayConfirmation] = React.useState(false);
  const [hasTriedSubmitting, setHasTriedSubmitting] = React.useState(false);
  const [checklistItems, setChecklistItems] = React.useState(viewModel.checklistItems);

  const isChecklistFinished = () => {
    if (checklistItems.length === 0) return true;

    return checklistItems.every((item) => item.state === "approved");
  };

  const canProgressToNextStep = () => {
    const allChecked = isChecklistFinished();

    if (!allChecked) {
      window.soneTools.scrollTo("#reviewChecklist");
    }

    setHasTriedSubmitting(true);

    return allChecked;
  };

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <SectionPanel.Section>
          <InvoiceV2InfoSection
            invoice={viewModel.details}
            stepOptions={{
              draft: { disabled: false },
              review: { disabled: false },
              pending: {
                disabled: !isChecklistFinished(),
                validate: canProgressToNextStep,
                onClick: () => setDisplayConfirmation(true),
              },
              completed: { disabled: true },
            }}
            activeStep="review"
            overDue={false}
            brands={viewModel.brands}
          />

          <RecipientsPanel
            initialViewers={viewModel.viewers}
            invoice={viewModel.details}
          />

          <DocumentChecklistPanel
            id="reviewChecklist"
            checklistItems={checklistItems}
            hasTriedSubmitting={hasTriedSubmitting}
            documentId={viewModel.details.documentId}
            onChecklistItemsChanged={setChecklistItems}
            setApi={window.CoreApi.Checklist.SetInvoiceV2ChecklistItemState}
            whoCanReviewApi={window.CoreApi.Checklist.WhoCanReviewInvoiceV2ChecklistItem}
            listApi={window.CoreApi.Checklist.ListInvoiceV2ChecklistItems}
          />

          <ReadonlyProductsPanelSegment productsPanel={viewModel.productsPanel} />

          {isPanelVisible(viewModel.linkedShowsPanel.config) && <ReadonlyLinkedShowsPanelSegment
            documentType="invoice"
            linkedShowsPanel={viewModel.linkedShowsPanel}
          />}

          <NotesPanel
            mainArea={"invoice"}
            useSectionPanel
            config={viewModel.linkedNotePanelConfig}
          />

          <SectionPanel.Panel>
            <ChangeLifecycleButtons
              documentType="invoice"
              documentId={viewModel.details.documentId}
              left="draft"
              right="pending"
              rightOnClick={() => setDisplayConfirmation(true)}
              rightValidate={canProgressToNextStep}
            />
          </SectionPanel.Panel>
        </SectionPanel.Section>

        <Modal
          isOpen={displayConfirmation}
          handleClose={() => setDisplayConfirmation(false)}
          width="tiny"
          title={RESX.Invoice.SendInvoice}
        >
          <SendOutDocument documentId={viewModel.details.documentId} />
        </Modal>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn>
        <InvoiceV2OptionsPanel
          optionsPanel={viewModel.optionsPanel}
          listUrl={viewModel.listUrl}
          invoice={viewModel.details}
          copyUrl={viewModel.copyUrl}
        />

        <InvoiceReminderApp
          document={viewModel.details}
          initialReminders={viewModel.reminders}
        />

        <ESignActivityPanel
          config={viewModel.activityPanelConfig}
          documentId={viewModel.details.documentId}
        />
      </TwoColumnLayout.RightColumn>
    </TwoColumnLayout>
  );
};

export default ReviewPanel;